/* global page */
// @ts-check

const workspaceSvc = require("./workspaceSvc.js");
const authApi = require("../backendapi/authApi.js");
const { store } = require("../state");

const {
    OWNER_ACTIONS,
    NEED_CURRENT_PROJECT_GROUP_WRITE_ACCESS,
    LABELER_ACTIONS,
    JD_ONLY_ADMINS_ACTIONS,
    JD_DISABLED_ACTIONS,
    hasPermission
} = require("../scripts/permissions");

function getDecodedToken(user) {
    try {
        const token_1 = user?.stsTokenManager?.accessToken?.split(".")?.[1];
        if (!token_1) {
            return {};
        }
        return JSON.parse(window.atob(token_1));
    } catch (e) {
        console.error(e); // eslint-disable-line
        return {};
    }
}

function getSigninAttributes(user) {
    const token = getDecodedToken(user);
    return token?.firebase?.sign_in_attributes || {};
}

const johndeere = {
    userIsAdmin(user) {
        const adminGroupName = "CVML-PROD-ADMIN"; // for staging, use "cvml-roboflow-admins"
        const ssoClaims = getSigninAttributes(user).cvmlAdGroups || [];
        return ssoClaims.includes(adminGroupName);
    },
    userHasPermission(user, action) {
        if (NEED_CURRENT_PROJECT_GROUP_WRITE_ACCESS.includes(action)) {
            if (!window.currentDataset?.group) {
                return this.userIsAdmin(user);
            }
            return userHasWritePermissionOnCurrentProjectGroup(action);
        }
        if (JD_DISABLED_ACTIONS.includes(action)) {
            return false;
        }
        if (!JD_ONLY_ADMINS_ACTIONS.includes(action)) {
            return true;
        }
        return this.userIsAdmin(user);
    }
};

let userDynamicPermissions = null;

async function initializeCustomRoles() {
    if (!window.project.isSSODomain()) {
        return;
    }
    const ssoConfig = await authApi.loadCustomSSOInfo();
    const user = window.user.toJSON();
    const additionalScopeFields = Array.isArray(ssoConfig?.additionalScopes)
        ? ssoConfig.additionalScopes
        : [];
    const authGroupFields = Array.isArray(ssoConfig?.authGroupAttributes)
        ? ssoConfig.authGroupAttributes
        : [];
    if (
        ssoConfig &&
        (additionalScopeFields.length > 0 || authGroupFields.length > 0) &&
        ssoConfig.customRoles &&
        Object.keys(ssoConfig.customRoles).length > 0
    ) {
        const ssoClaims = getSigninAttributes(user);
        const decodedToken = getDecodedToken(user);
        userDynamicPermissions = [];
        const userAdGroups = [];
        const userCustomRoles = [];
        for (const field of additionalScopeFields) {
            if (Array.isArray(ssoClaims[field])) {
                userAdGroups.push(...ssoClaims[field]);
            }
        }
        // authGroupAttributes are injected as sessionClaims by beforesignedin,
        // which places them as top-level claims on the decoded token
        for (const field of authGroupFields) {
            const groups = decodedToken[field] ?? ssoClaims[field];
            if (Array.isArray(groups)) {
                userAdGroups.push(...groups);
            }
        }
        for (const [roleId, roleConfig] of Object.entries(ssoConfig.customRoles)) {
            if (userAdGroups.includes(roleConfig.authGroup)) {
                userCustomRoles.push(roleId);
                if (roleConfig.admin) {
                    userDynamicPermissions.push(...OWNER_ACTIONS);
                } else if (Array.isArray(roleConfig.permissions)) {
                    userDynamicPermissions.push(...roleConfig.permissions);
                }
            }
        }
        userDynamicPermissions = [...new Set(userDynamicPermissions)];
        const noPermissionsAssigned = !userDynamicPermissions.length;
        if (noPermissionsAssigned) {
            // default role is the role applied when no roles are assigned
            const defaultRole = Object.values(ssoConfig.customRoles).find(
                (roleConfig) => roleConfig.default
            );
            if (defaultRole) {
                userDynamicPermissions = defaultRole.admin
                    ? OWNER_ACTIONS
                    : defaultRole.permissions;
            } else {
                // If no permissions are assigned, we default to labeler permissions because otherwise we get bugs in the app like infinite redirects
                userDynamicPermissions = LABELER_ACTIONS;
            }
        }
        window.eventTracker.event({
            type: "Initialize SSO Custom Roles",
            data: {
                userCustomRoles,
                userAdGroups,
                userDynamicPermissions,
                noPermissionsAssigned,
                domain: ssoConfig.domain
            }
        });
    }
}

const providerMap = { "oidc.john-deere-staging": johndeere, "oidc.john-deere": johndeere };

function userHasPermission(action) {
    const state = store.getState();
    const workspace =
        state.workspacesById?.[window?.currentWorkspace] ||
        window?.workspacesById?.[window?.currentWorkspace];

    let permission = hasPermission({ action, user: window.user, workspace });

    if (!workspace?.customRolesEnabled) {
        const provider = providerMap?.[window?.user?.providerData?.[0]?.providerId];
        if (provider) {
            permission &&= provider.userHasPermission(window?.user?.toJSON(), action);
        }
        if (userDynamicPermissions) {
            permission &&= userDynamicPermissions.includes(action);
            if (NEED_CURRENT_PROJECT_GROUP_WRITE_ACCESS.includes(action)) {
                permission &&= userHasWritePermissionOnCurrentProjectGroup();
            }
        }
    }

    logShadowPermissionMismatch(workspace, action, permission);

    return permission;
}

// Logs mismatches between current permission results and what workspace-level custom roles would produce,
// so we can validate the migration before enabling customRolesEnabled for real.
function logShadowPermissionMismatch(workspace, action, permission) {
    try {
        if (
            !workspace?.id ||
            !window?.user?.uid ||
            !workspace?.customRoles ||
            Object.keys(workspace.customRoles).length === 0 ||
            workspace.customRolesEnabled
        ) {
            return;
        }

        const shadowResult = hasPermission({
            action,
            user: window.user,
            workspace: { ...workspace, customRolesEnabled: true }
        });
        if (permission !== shadowResult) {
            window.eventTracker.event({
                type: "Shadow Action Permission Check",
                data: {
                    workspaceId: workspace.id,
                    userId: window.user.uid,
                    action,
                    actualResult: permission,
                    shadowResult,
                    resultDiffers: true,
                    source: "frontend",
                    checkType: "workspace_permission",
                    customRoleIds: workspace.userCustomRoles?.[window.user.uid] || []
                }
            });
        }
    } catch (e) {
        // never block the actual permission check
    }
}

function goToPreviousPage(pageContext) {
    const pathParts = pageContext.path.split("/");
    pathParts.pop();
    const redirectTo = pathParts.join("/");
    page.redirect(redirectTo);
}

function userHasWritePermissionOnCurrentProjectGroup() {
    const currentWorkspaceId = window.currentWorkspace;
    if (!currentWorkspaceId) {
        return false;
    }
    const currentGroupId = workspaceSvc.getCurrentGroupId();
    if (!currentGroupId) {
        return true; //
    }
    return workspaceSvc.hasAccessToProjectGroup(currentGroupId, currentWorkspaceId, "write");
}

module.exports = { initializeCustomRoles, userHasPermission, goToPreviousPage };
